import { useApp } from '../context';
import { linkProps, type Route } from '../router';
import { Avatar } from './Avatar';
import { NavClock } from './Screensaver';
import { useCurrentTheme } from '../theme';
import { useEffect, useState } from 'react';
import { call, type Policy } from '../api';
import { usePrefs } from '../prefs';
import { allNavEntries, visibleNav } from '../nav';
import { useTautulliStatus } from '../tautulli';


let cachedPolicy: { uuid: string; policy: Policy } | null = null;

export function usePolicy(uuid: string) {
  const [policy, setPolicy] = useState<Policy | null>(cachedPolicy?.uuid === uuid ? cachedPolicy.policy : null);
  useEffect(() => {
    call<{ policy: Policy }>('/api/policy').then((r) => { cachedPolicy = { uuid, policy: r.policy }; setPolicy(r.policy); }).catch(() => setPolicy(null));
  }, [uuid]);
  return policy;
}

export function TopNav({ route }: { route: Route }) {
  const { me, libraries, switchProfile } = useApp();
  const profile = me.profile!;
  const theme = useCurrentTheme();
  const prefs = usePrefs();
  // Profielbeheer: Live TV en Verzoeken alleen tonen als dit profiel ze mag gebruiken
  const policy = usePolicy(profile.uuid);
  const tautulli = useTautulliStatus(profile.uuid);
  const items = visibleNav(allNavEntries(libraries, policy ? { ...policy, server: tautulli?.inNav === true, plex: me.plex !== false } : null), prefs.nav);

  return (
    <header className="topnav">
      <a className="logo" {...linkProps('/')} aria-label="tumedia, naar Home" style={{ textDecoration: 'none', color: 'inherit' }}>
        tu<span>media</span>
      </a>
      <nav aria-label="Hoofdmenu" className="nav-main">
        {items.map((it) => (
          <a key={it.id} className="nav-item" {...linkProps(it.href)} aria-current={it.active(route) ? 'page' : undefined}>{it.label}</a>
        ))}
      </nav>
      <div className="spacer" />
      {theme?.components?.clock && <NavClock />}
      <a className="nav-item nav-icon" {...linkProps('/random')} aria-label="Wat zullen we kijken? (willekeurige titel)" title="Wat zullen we kijken?" aria-current={route.name === 'random' ? 'page' : undefined}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <rect x="3" y="3" width="18" height="18" rx="4" />
          <circle cx="8.5" cy="8.5" r="1.3" fill="currentColor" />
          <circle cx="15.5" cy="15.5" r="1.3" fill="currentColor" />
          <circle cx="15.5" cy="8.5" r="1.3" fill="currentColor" />
          <circle cx="8.5" cy="15.5" r="1.3" fill="currentColor" />
        </svg>
      </a>
      <a className="nav-item nav-icon" {...linkProps('/search')} aria-label="Zoeken" aria-current={route.name === 'search' ? 'page' : undefined}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <circle cx="11" cy="11" r="7" />
          <path d="M20 20l-4-4" />
        </svg>
      </a>
      <a className="nav-item nav-icon" {...linkProps('/settings')} aria-label="Instellingen" aria-current={route.name === 'settings' ? 'page' : undefined}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
        </svg>
      </a>
      <button className="nav-item nav-icon" onClick={switchProfile} aria-label={`Profiel wisselen (nu ${profile.title})`}>
        <Avatar src={profile.thumb} name={profile.title} className="nav-avatar" />
      </button>
    </header>
  );
}
